import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LicenseCheckAddInData } from "../models/LicenseCheckData";
import { LicenseCheckDisplayData } from "../models/LicenseCheckDisplayData";

import { NextCheckAddInData } from "../models/NextCheckData";
import licenseCheckRowsByUser from "../utils/licenseCheckRowsByUser";
import AddInIds from "@/constants/AddInIds";
import { useRawAddinData } from "@/hooks/addins/useAddinData";
import { useUsersWithKeys } from "@/hooks/users/useUsersWithKeys";

interface Args {
  results: string[];
  userTypes: string[];
  detailedView: boolean;
}

export default function useLatestLicenseChecks({ results, userTypes, detailedView }: Args) {
  const users = useUsersWithKeys();
  const [licenseCheckDisplayData, setLicenseCheckDisplayData] = useState<
    LicenseCheckDisplayData[] | null
  >(null);

  const {
    data: licenseCheckData = [],
    refetch: refetch,
    isLoading: isLoadingChecks,
  } = useRawAddinData<LicenseCheckAddInData>(AddInIds.licenseCheckData);
  const { data: nextCheckData = [], isLoading: isLoadingDates } =
    useRawAddinData<NextCheckAddInData>(AddInIds.nextLicenseCheckDate);
  const { t } = useTranslation();

  useEffect(() => {
    const licenseChecks = licenseCheckData.map(check => ({ id: check.id, ...check.details }));
    const nextCheckDates = nextCheckData.map(check => ({ id: check.id, ...check.details }));
    const licenseCheckDisplayData =
      users.data?.map((user, id) => {
        const checks = licenseCheckRowsByUser(user, id, licenseChecks, nextCheckDates);
        return detailedView
          ? checks
          : checks.reduce((prev, current) =>
              new Date(current.lastVerification).getTime() >
              new Date(prev.lastVerification).getTime()
                ? current
                : prev,
            );
      }) ?? [];
    setLicenseCheckDisplayData(licenseCheckDisplayData.flat());
  }, [detailedView, licenseCheckData, nextCheckData, t, users.data]);

  return {
    data:
      licenseCheckDisplayData?.filter(
        data =>
          (results.length === 0 || results.includes(data.statusMessage)) &&
          (userTypes.length === 0 || userTypes.includes(data.userType)),
      ) || [],
    refetch: refetch,
    isLoading: isLoadingChecks || isLoadingDates,
  };
}
